import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowRightLeft, Check, Copy, CopyPlus, GripVertical, ImageIcon, MoreVertical, Pencil, Timer, Trash2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { pointBadge, TYPE_LABELS } from "@/lib/chem";

const chip = "rounded-md px-3 py-2 text-sm border border-[rgba(71,85,105,0.45)] text-slate-300";

const Structured = ({ q }) => {
  const d = q.data || {};
  if (q.type === "guess") {
    return (
      <div className="flex flex-wrap gap-2 mt-3" data-testid="question-guess-answers">
        {(d.answers || []).map((a) => <span key={a} className="rounded-md px-3 py-2 text-sm border border-[#34D399]/60 bg-[#34D399]/10 text-[#6EE7B7] font-semibold">{a}</span>)}
      </div>
    );
  }
  if (q.type === "matching") {
    return (
      <div className="grid sm:grid-cols-2 gap-2 mt-3" data-testid="question-pairs">
        {(d.pairs || []).map((p) => <div key={p.id} className={chip}>{p.left} <span className="text-[#FACC15] font-bold">↔</span> {p.right}</div>)}
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-2 mt-3" data-testid="question-categories">
      {(d.categories || []).map((c) => (
        <div key={c.id} className={chip}>
          <b className="text-[#6EE7B7]">{c.name}:</b> {(d.items || []).filter((i) => i.category_id === c.id).map((i) => i.text).join(", ")}
        </div>
      ))}
    </div>
  );
};

const Options = ({ q }) => ["matching", "classification", "guess"].includes(q.type) ? <Structured q={q} /> : (
  <div className="grid sm:grid-cols-2 gap-2 mt-3">
    {q.options.map((o) => {
      const ok = o.id === q.correct_option_id;
      return (
        <div key={o.id} className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm border ${ok ? "border-[#34D399]/60 bg-[#34D399]/10 text-[#6EE7B7] font-semibold" : "border-[rgba(71,85,105,0.45)] text-slate-300"}`}>
          <span className="font-mono-x text-xs uppercase opacity-70">{o.id}</span>
          <span className="flex-1">{o.text}</span>
          {ok && <Check className="h-4 w-4" />}
          {!ok && o.misconception && <span className="eyebrow text-[0.6rem] text-amber-400" title={o.misconception}>yanılgı</span>}
        </div>
      );
    })}
  </div>
);

const RowMenu = ({ q, actions }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="p-2 rounded-md hover:bg-[rgba(71,85,105,0.45)]" data-testid={`question-menu-${q.id}`} aria-label="Soru işlemleri">
        <MoreVertical className="h-5 w-5" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={() => actions.edit(q)} data-testid="question-edit-action"><Pencil className="h-4 w-4 mr-2" />Düzenle</DropdownMenuItem>
      <DropdownMenuItem onClick={() => actions.duplicate(q)} data-testid="question-duplicate-action"><Copy className="h-4 w-4 mr-2" />Çoğalt</DropdownMenuItem>
      <DropdownMenuItem onClick={() => actions.transfer(q, "copy")} data-testid="question-copy-action"><CopyPlus className="h-4 w-4 mr-2" />Başka havuza kopyala</DropdownMenuItem>
      <DropdownMenuItem onClick={() => actions.transfer(q, "move")} data-testid="question-move-action"><ArrowRightLeft className="h-4 w-4 mr-2" />Başka havuza taşı</DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => actions.remove(q)} className="text-red-400" data-testid="question-delete-action"><Trash2 className="h-4 w-4 mr-2" />Sil</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const QuestionRow = ({ q, index, dragDisabled, actions }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id, disabled: dragDisabled });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} data-testid={`question-item-${index + 1}`}
      className={`panel p-5 flex gap-4 transition-[background-color,border-color,box-shadow] duration-200 ${isDragging ? "border-[#FACC15] glow-mustard relative z-10" : "hover:bg-white/10 hover:border-slate-500/60"}`}>
      <button {...attributes} {...listeners} disabled={dragDisabled} aria-label="Sürükle"
        className={`self-start pt-1 ${dragDisabled ? "text-slate-700 cursor-not-allowed" : "text-slate-400 hover:text-[#FACC15] cursor-grab active:cursor-grabbing"}`}
        data-testid={`question-drag-${index + 1}`}>
        <GripVertical className="h-6 w-6" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono-x text-sm text-slate-500">#{index + 1}</span>
          <span className={`px-2.5 py-0.5 rounded-md border text-sm font-bold ${pointBadge(q.points)}`} data-testid="question-points">{q.points} puan</span>
          <span className="eyebrow">{TYPE_LABELS[q.type] || q.type}</span>
          <span className="flex items-center gap-1 text-sm text-slate-400"><Timer className="h-4 w-4" />{q.time_limit} sn</span>
          {q.topic && <span className="text-xs px-2 py-0.5 rounded-full bg-[rgba(71,85,105,0.45)] text-slate-200">{q.topic}</span>}
          {q.image && <ImageIcon className="h-4 w-4 text-slate-400" />}
        </div>
        <p className="mt-2 text-lg font-semibold text-white break-words" data-testid="question-text">{q.text}</p>
        <Options q={q} />
      </div>
      <div className="self-start"><RowMenu q={q} actions={actions} /></div>
    </div>
  );
};
